import type { ImageType } from '@eliadmin/core/src/api/api-types';

export const TOWN_SLUGS = [
  'oprava-preliacin-presov',
  'oprava-preliacin-kosice',
  'oprava-preliacin-bardejov',
  'oprava-preliacin-poprad',
  'oprava-preliacin-michalovce',
  'oprava-preliacin-humenne',
  'oprava-preliacin-lipany',
  'oprava-preliacin-sabinov',
  'oprava-preliacin-spisska-nova-ves',
  'oprava-preliacin-stara-lubovna',
  'oprava-preliacin-svidnik',
  'oprava-preliacin-vranov-nad-toplou',
] as const;

export type TownSlug = (typeof TOWN_SLUGS)[number];

export const TOWN_SLUG = 'mesta';

export const TOWN_SLUG_TO_TOWN_NAME = {
  'oprava-preliacin-presov': 'Prešov',
  'oprava-preliacin-kosice': 'Košice',
  'oprava-preliacin-bardejov': 'Bardejov',
  'oprava-preliacin-poprad': 'Poprad',
  'oprava-preliacin-michalovce': 'Michalovce',
  'oprava-preliacin-humenne': 'Humenné',
  'oprava-preliacin-lipany': 'Lipany',
  'oprava-preliacin-sabinov': 'Sabinov',
  'oprava-preliacin-spisska-nova-ves': 'Spišská Nová Ves',
  'oprava-preliacin-stara-lubovna': 'Stará Ľubovňa',
  'oprava-preliacin-svidnik': 'Svidník',
  'oprava-preliacin-vranov-nad-toplou': 'Vranov nad Topľou',
} as const;

export const TOWN_NAME_TO_TOWN_SLUG = {
  Prešov: 'oprava-preliacin-presov',
  Košice: 'oprava-preliacin-kosice',
  Bardejov: 'oprava-preliacin-bardejov',
  Poprad: 'oprava-preliacin-poprad',
  Michalovce: 'oprava-preliacin-michalovce',
  Humenné: 'oprava-preliacin-humenne',
  Lipany: 'oprava-preliacin-lipany',
  Sabinov: 'oprava-preliacin-sabinov',
  'Spišská Nová Ves': 'oprava-preliacin-spisska-nova-ves',
  'Stará Ľubovňa': 'oprava-preliacin-stara-lubovna',
  Svidník: 'oprava-preliacin-svidnik',
  'Vranov nad Topľou': 'oprava-preliacin-vranov-nad-toplou',
} as const;

export const TOWN_ENTRIES = Object.entries(TOWN_SLUG_TO_TOWN_NAME).map(([slug, name]) => ({
  slug: slug as TownSlug,
  name,
}));

export type TownImageCredit = {
  sourceUrl: string;
  author: string;
  license: string;
  licenseUrl: string;
};

export const TOWN_IMAGE_CREDITS: Partial<Record<TownSlug, TownImageCredit>> = {
  'oprava-preliacin-humenne': {
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Humenn%C3%A9,_centrum_m%C4%9Bsta.jpg',
    author: 'Vojtěch Dočkal',
    license: 'CC BY-SA 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
  },
  'oprava-preliacin-lipany': {
    sourceUrl:
      'https://commons.wikimedia.org/wiki/File:Kostol_sv._Martina._Lipany_25_Slovakia_8.jpg',
    author: 'Jozef Kotulič',
    license: 'CC BY 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by/4.0/',
  },
  'oprava-preliacin-sabinov': {
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:CentreWMPSabinov13Slovakia34.JPG',
    author: 'Jozef Kotulič',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
  },
  'oprava-preliacin-spisska-nova-ves': {
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Spi%C5%A1sk%C3%A1_Nov%C3%A1_Ves_063.jpg',
    author: 'Andrej Macko',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
  },
  'oprava-preliacin-stara-lubovna': {
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Stara_Lubovna_castle_ID_710-9750.JPG',
    author: 'Miro Švorc',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
  },
  'oprava-preliacin-svidnik': {
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Svidnik_pesia_zona.jpg',
    author: 'Peter Zelizňák',
    license: 'Public domain',
    licenseUrl: 'https://creativecommons.org/publicdomain/mark/1.0/',
  },
  'oprava-preliacin-vranov-nad-toplou': {
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Vranov_17_Slovakia30.jpg',
    author: 'Jozef Kotulič',
    license: 'CC BY-SA 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
  },
};

const normalizeCityImageKey = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9]+/g, '-');

const getTownImageKey = (slug: TownSlug) => slug.replace(/^oprava-preliacin-/, '');

export const mapTownImages = (images: readonly ImageType[] = []) => {
  const imagesByCity = new Map<string, ImageType>();

  images.forEach(image => {
    [image.custom_title, image.name].forEach(candidate => {
      if (!candidate) return;

      const key = normalizeCityImageKey(candidate);
      if (key && !imagesByCity.has(key)) imagesByCity.set(key, image);
    });
  });

  return TOWN_ENTRIES.map(town => ({
    ...town,
    image: imagesByCity.get(getTownImageKey(town.slug)),
  }));
};

export const getTownInitials = (name: string) =>
  name
    .split(/\s+/)
    .map(part => part[0])
    .join('')
    .slice(0, 3)
    .toLocaleUpperCase('sk');
